// This code mostly comes from the following post
// https://github.com/mapbox/mapbox-gl-js/issues/5783

import lodashKeys from "lodash/keys";

export { _layerClickEventHandlerOn as layerClickEventHandlerOn };
export { _layerClickEventHandlerOff as layerClickEventHandlerOff };
export { _layerEventHandlerOn as layerEventHandlerOn };
export { _layerEventHandlerOff as layerEventHandlerOff };

let handlers = {};
let defaultHandlers = {};

const _layerClickEventHandlerOn = (map, layerId, callback) => {
  _layerEventHandlerOn(map, "click", layerId, callback);
};

const _layerClickEventHandlerOff = (map, layerId) => {
  _layerEventHandlerOff(map, "click", layerId);
};

const _layerEventHandlerOn = (map, eventName, layerId, callback) => {
  if (map) {
    // Create new event name keys in our storage maps
    if (!defaultHandlers[eventName]) {
      defaultHandlers[eventName] = [];
    }
    if (!handlers[eventName]) {
      handlers[eventName] = {};
    }

    if (
      !Object.keys(handlers[eventName]).length ||
      !_currentMapExistsInHandlers(map, eventName)
    ) {
      // Register a map event for the given event name
      map.on(eventName, (event) => {
        _onMapEvent(event, map);
      });
    }

    if (!layerId) {
      // layerId is not specified, so this is a 'default handler' that will be called if no other events have cancelled the event
      defaultHandlers[eventName].push(callback);
    } else {
      // layerId is specified, so this is a specific handler for that layer
      handlers[eventName][`map-${map._mapId}-${layerId}`] = callback;
    }
  }
};

const _layerEventHandlerOff = (map, eventName, layerId) => {
  if (
    map &&
    handlers[eventName] &&
    handlers[eventName][`map-${map._mapId}-${layerId}`]
  ) {
    handlers[eventName][`map-${map._mapId}-${layerId}`] = null;
    delete handlers[eventName][`map-${map._mapId}-${layerId}`];
    const EVENT_KEYS = lodashKeys(handlers[eventName]);
    if (!EVENT_KEYS.length) {
      delete handlers[eventName];
      delete map._listeners[eventName];
    }
  }
};

const _getHandleLayers = (eventName, map) => {
  if (!handlers[eventName]) {
    return [];
  }
  let layers = [];
  const keys = Object.keys(handlers[eventName]);
  keys.forEach((key) => {
    if (key.indexOf(`map-${map._mapId}-`) > -1) {
      layers.push(key.replace(`map-${map._mapId}-`, ""));
    }
  });
  return layers;
};

const _onMapEvent = (event, map) => {
  const EVENT_NAME = event.type;
  // unordered list of layers to be checked
  let layers = _getHandleLayers(EVENT_NAME, map);

  // This gets the features that were clicked in the correct layer order
  let eventFeatures = map.queryRenderedFeatures(event.point, {
    layers: layers,
  });

  // This makes a sorted array of the layers that are clicked
  let sortedLayers = eventFeatures.reduce((sorted, next) => {
    let nextLayerId = next.layer.id;
    if (sorted.indexOf(nextLayerId) === -1) {
      return sorted.concat([nextLayerId]);
    }
    return sorted;
  }, []);

  // Add the layers and features info to the event
  event.eventLayers = sortedLayers;
  event.eventFeatures = eventFeatures;

  let bubbleEvent = true;

  // Loop through each of the sorted layers starting with the first (top-most clicked layer)
  // Call the handler for each layer in order, and potentially stop propagating the event
  for (let i = 0; i < sortedLayers.length; i++) {
    if (bubbleEvent !== true) {
      break;
    }
    let layerId = sortedLayers[i];

    // Filter out only the features for this layer
    let layerFeatures = eventFeatures.filter((feature) => {
      return feature.layer.id === layerId;
    });

    // Call the layer handler for this layer, giving the clicked features
    bubbleEvent = handlers[EVENT_NAME][`map-${map._mapId}-${layerId}`](
      event,
      layerFeatures
    );
  }

  if (bubbleEvent === true) {
    // No events have cancelled the bubbling
    if (!defaultHandlers[EVENT_NAME]) {
      return;
    }

    defaultHandlers[EVENT_NAME].forEach((handler) => {
      handler(event);
    });
  }
};

const _currentMapExistsInHandlers = (map, eventName) => {
  let exists = false;
  Object.keys(handlers[eventName]).forEach((key) => {
    if (key.indexOf(`map-${map._mapId}-`) > -1) {
      exists = true;
    }
  });
  return exists;
};
